<template>
  <button
    ref="button"
    :class="buttonClasses"
    :disabled="disabled || loading"
    :type="type"
    @click="handleClick"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
    @focus="handleFocus"
    @blur="handleBlur"
  >
    <!-- Loading spinner -->
    <div
      v-if="loading"
      class="custom-btn__loader"
    >
      <div class="custom-btn__loader__spinner" />
    </div>

    <!-- Button content -->
    <div
      v-else
      class="custom-btn__content"
    >
      <!-- Left icon -->
      <div
        v-if="leftIcon"
        class="custom-btn__icon custom-btn__icon--left"
      >
        <i :class="leftIcon" />
      </div>

      <!-- Button text/content -->
      <span
        v-if="$slots.default"
        class="custom-btn__text"
      >
        <slot />
      </span>

      <!-- Right icon -->
      <div
        v-if="rightIcon"
        class="custom-btn__icon custom-btn__icon--right"
      >
        <i :class="rightIcon" />
      </div>
    </div>
  </button>
</template>

<script>
export default {
  name: "CustomButton",
  inheritAttrs: false,
  props: {
    // Button type
    type: {
      type: String,
      default: "button",
      validator: (value) => ["button", "submit", "reset"].includes(value),
    },

    // Color variants
    color: {
      type: String,
      default: "primary",
      validator: (value) =>
        [
          "primary",
          "secondary",
          "success",
          "warning",
          "error",
          "info",
        ].includes(value),
    },

    // Button variants
    variant: {
      type: String,
      default: "elevated",
      validator: (value) =>
        ["elevated", "outlined", "text", "flat", "tonal"].includes(value),
    },

    // Sizes
    size: {
      type: String,
      default: "default",
      validator: (value) =>
        ["x-small", "small", "default", "large", "x-large"].includes(value),
    },

    // States
    disabled: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    // Icons
    leftIcon: {
      type: String,
      default: null,
    },

    rightIcon: {
      type: String,
      default: null,
    },

    // Block button
    block: {
      type: Boolean,
      default: false,
    },

    // Icon only button
    icon: {
      type: Boolean,
      default: false,
    },

    // Rounded corners
    rounded: {
      type: Boolean,
      default: false,
    },

    // Depressed (no elevation)
    depressed: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    buttonClasses() {
      return [
        "custom-btn",
        `custom-btn--${this.color}`,
        `custom-btn--${this.variant}`,
        `custom-btn--${this.size}`,
        {
          "custom-btn--disabled": this.disabled,
          "custom-btn--loading": this.loading,
          "custom-btn--block": this.block,
          "custom-btn--icon": this.icon,
          "custom-btn--rounded": this.rounded,
          "custom-btn--depressed": this.depressed,
        },
      ];
    },
  },

  methods: {
    handleClick(event) {
      if (this.disabled || this.loading) {
        event.preventDefault();
        return;
      }
      this.$emit("click", event);
    },

    handleMouseEnter(event) {
      if (!this.disabled && !this.loading) {
        this.$emit("mouseenter", event);
      }
    },

    handleMouseLeave(event) {
      if (!this.disabled && !this.loading) {
        this.$emit("mouseleave", event);
      }
    },

    handleFocus(event) {
      if (!this.disabled && !this.loading) {
        this.$emit("focus", event);
      }
    },

    handleBlur(event) {
      if (!this.disabled && !this.loading) {
        this.$emit("blur", event);
      }
    },
  },
};
</script>

<style scoped>
.custom-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: var(--radius-md);
  font-family: inherit;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: var(--transition-default);
  user-select: none;
  outline: none;
  text-decoration: none;
  overflow: hidden;
  backdrop-filter: blur(10px);
}

/* Base button styles */
.custom-btn {
  background-color: var(--color-primary);
  color: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.custom-btn:hover:not(.custom-btn--disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  background-color: var(--color-primary-light);
}

.custom-btn:active:not(.custom-btn--disabled) {
  transform: translateY(0);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Color variants */
.custom-btn--primary {
  background-color: var(--color-primary);
  color: white;
}

.custom-btn--primary:hover:not(.custom-btn--disabled) {
  background-color: var(--color-primary-light);
}

.custom-btn--secondary {
  background-color: var(--color-secondary);
  color: white;
}

.custom-btn--secondary:hover:not(.custom-btn--disabled) {
  background-color: var(--color-secondary-light);
}

.custom-btn--success {
  background-color: var(--color-success);
  color: white;
}

.custom-btn--success:hover:not(.custom-btn--disabled) {
  background-color: #24a06b;
}

.custom-btn--warning {
  background-color: var(--color-warning);
  color: white;
}

.custom-btn--warning:hover:not(.custom-btn--disabled) {
  background-color: #e6951a;
}

.custom-btn--error {
  background-color: var(--color-error);
  color: white;
}

.custom-btn--error:hover:not(.custom-btn--disabled) {
  background-color: #e63939;
}

.custom-btn--info {
  background-color: var(--color-info);
  color: white;
}

.custom-btn--info:hover:not(.custom-btn--disabled) {
  background-color: #2fa8e3;
}

/* Button variants */
.custom-btn--outlined {
  background-color: transparent;
  border: 2px solid var(--color-primary);
  color: var(--color-primary);
  box-shadow: none;
}

.custom-btn--outlined:hover:not(.custom-btn--disabled) {
  background-color: var(--color-primary);
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.custom-btn--text {
  background-color: transparent;
  color: var(--color-primary);
  box-shadow: none;
}

.custom-btn--text:hover:not(.custom-btn--disabled) {
  background-color: rgba(21, 72, 102, 0.1);
  transform: none;
  box-shadow: none;
}

.custom-btn--flat {
  background-color: transparent;
  color: var(--color-primary);
  box-shadow: none;
}

.custom-btn--flat:hover:not(.custom-btn--disabled) {
  background-color: rgba(21, 72, 102, 0.1);
  transform: none;
  box-shadow: none;
}

.custom-btn--tonal {
  background-color: rgba(21, 72, 102, 0.1);
  color: var(--color-primary);
  box-shadow: none;
}

.custom-btn--tonal:hover:not(.custom-btn--disabled) {
  background-color: rgba(21, 72, 102, 0.2);
  transform: none;
  box-shadow: none;
}

/* Size variations */
.custom-btn--x-small {
  font-size: 12px;
  padding: 0 12px;
  height: 28px;
  min-width: 28px;
}

.custom-btn--small {
  font-size: 14px;
  padding: 0 16px;
  height: 32px;
  min-width: 32px;
}

.custom-btn--default {
  font-size: 14px;
  padding: 0 20px;
  height: 40px;
  min-width: 40px;
}

.custom-btn--large {
  font-size: 16px;
  padding: 0 24px;
  height: 48px;
  min-width: 48px;
}

.custom-btn--x-large {
  font-size: 18px;
  padding: 0 32px;
  height: 56px;
  min-width: 56px;
}

/* Icon button */
.custom-btn--icon {
  border-radius: 50%;
  min-width: auto;
  padding: 0;
}

.custom-btn--icon.custom-btn--x-small {
  width: 28px;
  height: 28px;
}

.custom-btn--icon.custom-btn--small {
  width: 32px;
  height: 32px;
}

.custom-btn--icon.custom-btn--default {
  width: 40px;
  height: 40px;
}

.custom-btn--icon.custom-btn--large {
  width: 48px;
  height: 48px;
}

.custom-btn--icon.custom-btn--x-large {
  width: 56px;
  height: 56px;
}

/* Block button */
.custom-btn--block {
  width: 100%;
}

/* Rounded button */
.custom-btn--rounded {
  border-radius: 50px;
}

/* Depressed button */
.custom-btn--depressed {
  box-shadow: none;
}

.custom-btn--depressed:hover:not(.custom-btn--disabled) {
  box-shadow: none;
}

/* Disabled state */
.custom-btn--disabled,
.custom-btn--disabled:hover,
.custom-btn--disabled:active {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
  box-shadow: none !important;
  background-color: var(--color-neutral) !important;
  color: var(--color-text-muted) !important;
  pointer-events: none;
}

/* Loading state */
.custom-btn--loading {
  pointer-events: none;
}

/* Button content */
.custom-btn__content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 100%;
}

.custom-btn__text {
  white-space: nowrap;
}

/* Icons */
.custom-btn__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: inherit;
}

.custom-btn__icon--left {
  margin-right: 4px;
}

.custom-btn__icon--right {
  margin-left: 4px;
}

/* Loading spinner */
.custom-btn__loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.custom-btn__loader__spinner {
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Focus state */
.custom-btn:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}

/* Animation for button press */
@keyframes buttonPress {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.98);
  }
  100% {
    transform: scale(1);
  }
}

.custom-btn:active:not(.custom-btn--disabled) {
  animation: buttonPress 0.1s ease-out;
}
</style>
